import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Layers, UploadCloud, AlertCircle, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { SocialPlatform } from "@/types/user";
import logo from '../assets/images/logo-artie.png';

// Platforms supported for screenshot upload in this step
const PLATFORMS: { id: SocialPlatform | "apple_podcasts"; label: string }[] = [
  { id: "instagram", label: "Instagram" },
  { id: "tiktok", label: "TikTok" },
  { id: "spotify", label: "Spotify" },
  { id: "apple_podcasts", label: "Apple Podcasts" },
];

export default function Onboarding4() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuthStore();
  const [selectedPlatform, setSelectedPlatform] = useState<string>("instagram");
  const [isUploading, setIsUploading] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [uploadedScreenshots, setUploadedScreenshots] = useState<{ platform: string; url: string; key: string; analysisData?: any }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const cleanNumber = (val: any) => {
     if (typeof val === 'number') return val;
     if (typeof val === 'string') {
         const cleaned = val.replace(/[,%\s]/g, '');
         const num = Number(cleaned);
         return isNaN(num) ? undefined : num;
     }
     return undefined;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // 1. Init upload
      const initData = await apiClient.initScreenshotUpload(file.name, file.type || "image/jpeg");
      
      // 2. Upload to S3
      const formData = new FormData();
      Object.entries(initData.fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append("file", file);

      await fetch(initData.upload_url, {
        method: "POST",
        body: formData,
        mode: 'no-cors'
      });

      // 3. Analyze/Complete
      toast.info("Analyzing screenshot...");
      const result = await apiClient.completeScreenshotUpload(initData.s3_key, selectedPlatform);
      
      // 4. Update local state
      // We store the key/url to display it.
      const blobUrl = URL.createObjectURL(file);
      
      setUploadedScreenshots(prev => [...prev, {
        platform: selectedPlatform,
        url: blobUrl,
        key: initData.s3_key,
        analysisData: result.data
      }]);

      toast.success("Screenshot uploaded successfully");

    } catch (error) {
      console.error(error);
      toast.error("Failed to upload screenshot");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleContinue = async () => {
    if (uploadedScreenshots.length < 2) {
      toast.error("Please provide at least 2 screenshots as requested.");
      return;
    }

    // We need to merge the uploaded screenshots into the user's social profiles
    // and explicitly save the updated user object.
    
    try {
        const currentProfiles = user?.social_profiles || [];
        
        // Helper to update or create profile for a platform
        const updateProfiles = (profiles: any[], upload: { platform: string; url: string; key: string; analysisData?: any }) => {
            const platform = upload.platform as SocialPlatform;
            const existingProfileIndex = profiles.findIndex(p => p.platform === platform);
            
            const analysisData = upload.analysisData || {};
            const extractedHandle = analysisData.username || analysisData.channel_name || "";
            const extractedFollowers = cleanNumber(analysisData.followers_count || analysisData.subscribers_count);

            if (existingProfileIndex >= 0) {
                // Update existing profile
                const profile = profiles[existingProfileIndex];
                const currentScreenshots = profile.screenshots || [];
                
                let updatedProfile = { ...profile };
                
                // Update handle if missing and we found one
                if ((!updatedProfile.handle || updatedProfile.handle === "") && extractedHandle) {
                    updatedProfile.handle = extractedHandle;
                }
                
                // Update followers if missing and we found some
                if ((!updatedProfile.follower_count) && extractedFollowers) {
                    updatedProfile.follower_count = extractedFollowers;
                }

                // Avoid duplicates
                if (!currentScreenshots.includes(upload.key)) {
                    updatedProfile.screenshots = [...currentScreenshots, upload.key];
                }
                
                profiles[existingProfileIndex] = updatedProfile;

            } else {
                // Create new profile for this platform
                profiles.push({
                    platform: platform,
                    handle: extractedHandle, // Use extracted handle if available
                    follower_count: extractedFollowers,
                    screenshots: [upload.key]
                });
            }
            return profiles;
        };

        // Apply all uploads to the profiles
        let updatedProfiles = [...currentProfiles];
        for (const upload of uploadedScreenshots) {
            updatedProfiles = updateProfiles(updatedProfiles, upload);
        }

        // Save to backend
        await apiClient.updateCurrentUser({
            social_profiles: updatedProfiles,
            onboarding_completed: true
        });
        
        await checkAuth(); // Refresh user state
        toast.success("Profile updated successfully!");
        navigate("/dashboard");
    } catch (e) {
        console.error(e);
        toast.error("Failed to save profile changes.");
    }
  };

  const handleRemoveScreenshot = (index: number) => {
    setUploadedScreenshots(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <style>{`
        /* CSS Variables - Orange Palette (Copied from HTML) */
        :root {
          --background: oklch(1.0000 0 0);
          --foreground: oklch(0.2686 0 0);
          --card: oklch(1.0000 0 0);
          --card-foreground: oklch(0.2686 0 0);
          --popover: oklch(1.0000 0 0);
          --popover-foreground: oklch(0.2686 0 0);
          --primary: oklch(0.7686 0.1647 70.0804);
          --primary-foreground: oklch(0 0 0);
          --secondary: oklch(0.9670 0.0029 264.5419);
          --secondary-foreground: oklch(0.4461 0.0263 256.8018);
          --muted: oklch(0.9846 0.0017 247.8389);
          --muted-foreground: oklch(0.5510 0.0234 264.3637);
          --accent: oklch(0.9869 0.0214 95.2774);
          --accent-foreground: oklch(0.4732 0.1247 46.2007);
          --destructive: oklch(0.6368 0.2078 25.3313);
          --destructive-foreground: oklch(1.0000 0 0);
          --border: oklch(0.9276 0.0058 264.5313);
          --input: oklch(0.9276 0.0058 264.5313);
          --ring: oklch(0.7686 0.1647 70.0804);
          --radius: 0.375rem;
        }
        .dark {
          --background: oklch(0.2046 0 0);
          --foreground: oklch(0.9219 0 0);
          --card: oklch(0.2686 0 0);
          --card-foreground: oklch(0.9219 0 0);
          --popover: oklch(0.2686 0 0);
          --popover-foreground: oklch(0.9219 0 0);
          --primary: oklch(0.7686 0.1647 70.0804);
          --primary-foreground: oklch(0 0 0);
          --secondary: oklch(0.2686 0 0);
          --secondary-foreground: oklch(0.9219 0 0);
          --muted: oklch(0.2393 0 0);
          --muted-foreground: oklch(0.7155 0 0);
          --accent: oklch(0.4732 0.1247 46.2007);
          --accent-foreground: oklch(0.9243 0.1151 95.7459);
          --destructive: oklch(0.6368 0.2078 25.3313);
          --destructive-foreground: oklch(1.0000 0 0);
          --border: oklch(0.3715 0 0);
          --input: oklch(0.3715 0 0);
          --ring: oklch(0.7686 0.1647 70.0804);
        }
        /* Font Families */
        .font-cabinet { font-family: 'Cabinet Grotesk', sans-serif; }
        .font-ibm { font-family: 'IBM Plex Sans', sans-serif; }
        body {
          background-color: var(--background);
          color: var(--foreground);
          -webkit-font-smoothing: antialiased;
          font-family: 'IBM Plex Sans', sans-serif;
        }
        /* Animations */
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slideUp 0.5s ease-out forwards;
        }
        .delay-100 { animation-delay: 100ms; }
        .delay-200 { animation-delay: 200ms; }
        .delay-300 { animation-delay: 300ms; }
        .delay-400 { animation-delay: 400ms; }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
      
      <div className="min-h-screen selection:bg-[var(--primary)] selection:text-[var(--primary-foreground)] relative bg-[var(--background)] text-[var(--foreground)] overflow-x-hidden flex flex-col items-center">

        {/* Top Navigation */}
        <nav className="w-full max-w-5xl px-6 py-6 flex items-center justify-between z-50 relative">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full bg-[oklch(1.0000_0_0)] border border-[oklch(0.92_0_0)] flex items-center justify-center text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:border-[oklch(0.2686_0_0)] transition-all"
            >
              <ArrowLeft className="w-5 h-5 stroke-[1.5]" />
            </button>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex gap-1">
              <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
              <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
              <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
              <div className="w-2 h-2 rounded-full bg-[oklch(0.70_0.18_55)]"></div>
            </div>
            <button
              onClick={() => setIsSkipping(true)}
              className="text-xs font-medium text-[var(--foreground)] hover:text-[var(--primary)] transition-colors"
            >
              Skip
            </button>
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 z-10 flex flex-col w-full max-w-2xl pt-8 pr-6 pb-32 pl-6 items-center">
          {/* Header - Artie Guidance */}
          <div className="w-full max-w-lg mb-10 animate-slide-up">
            <div className="flex items-start gap-5 bg-[var(--card)] p-6 rounded-2xl border border-[var(--border)] shadow-sm">
               <div className="hidden sm:block shrink-0">
                 <div className="w-16 h-16 rounded-full bg-[var(--secondary)]/30 border-2 border-[var(--background)] shadow-md overflow-hidden">
                    <img src={logo} alt="Artie" className="w-full h-full object-cover" />
                 </div>
               </div>
               <div className="space-y-1">
                 <h1 className="text-2xl font-bold font-cabinet text-[var(--foreground)]">
                    Verify other platforms
                 </h1>
                 <p className="text-[var(--muted-foreground)] font-ibm text-sm leading-relaxed">
                    For platforms without API access like Instagram or TikTok, I can analyze screenshots of your profile and analytics to give you credit for your full reach.
                 </p>
               </div>
            </div>
          </div>

          {/* Section 1: Evidence (Cards) */}
          <div className="w-full mb-8 animate-slide-up delay-100">
            <div className="w-full bg-[var(--card)] border border-[var(--border)] rounded-2xl p-8 md:p-12 flex flex-col items-center text-center shadow-sm relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-b from-[var(--secondary)]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

              <div className="relative z-10 flex flex-col items-center w-full max-w-md">
                <div className="w-16 h-16 bg-[var(--muted)] rounded-2xl flex items-center justify-center mb-6 text-[var(--foreground)] shadow-sm ring-1 ring-inset ring-black/5">
                  <Layers className="w-8 h-8" />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold font-cabinet text-[var(--foreground)] mb-2 tracking-tight">
                  Upload Screenshots
                </h3>
                
                {/* Platform Selection Pills */}
                <div className="w-full mb-8">
                  <div className="text-center mb-5">
                    <p className="text-sm text-[var(--muted-foreground)] leading-relaxed">
                      Select the platform you want to upload screenshots for. You can switch between them to upload evidence for multiple platforms.
                    </p>
                  </div>
                  
                  <div className="flex flex-wrap justify-center gap-3">
                    {PLATFORMS.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setSelectedPlatform(p.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium font-ibm transition-all cursor-pointer border ${
                          selectedPlatform === p.id
                            ? "bg-[var(--foreground)] text-[var(--background)] border-[var(--foreground)] shadow-md scale-105"
                            : "bg-[var(--background)] text-[var(--muted-foreground)] border-[var(--border)] hover:border-[var(--foreground)] hover:text-[var(--foreground)] hover:bg-[var(--muted)]"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Upload Box */}
                <div 
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-[var(--border)] rounded-2xl p-8 mb-4 flex flex-col items-center justify-center text-center hover:bg-[var(--muted)]/50 transition-all cursor-pointer group/upload bg-[var(--background)]/50 relative overflow-hidden"
                >
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    className="hidden" 
                    accept="image/*"
                    onChange={handleFileSelect}
                    disabled={isUploading}
                  />
                  
                  {isUploading ? (
                    <div className="flex flex-col items-center justify-center py-2">
                        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)] mb-2" />
                        <p className="text-sm font-medium text-[var(--foreground)]">Uploading & Analyzing...</p>
                    </div>
                  ) : (
                    <>
                      <div className="w-12 h-12 rounded-full bg-[var(--card)] shadow-md ring-1 ring-black/5 flex items-center justify-center mb-3 group-hover/upload:scale-110 group-hover/upload:shadow-lg transition-all z-10">
                        <UploadCloud className="w-6 h-6 text-[var(--primary)]" />
                      </div>
                      <p className="text-sm font-medium text-[var(--foreground)] mb-1 font-ibm z-10">
                        Click to upload {PLATFORMS.find(p => p.id === selectedPlatform)?.label} screenshots
                      </p>
                      <p className="text-xs text-[var(--muted-foreground)] font-ibm z-10">
                        Profile Overview & Analytics (Max 10MB)
                      </p>
                    </>
                  )}
                </div>

                {/* Uploaded Screenshots Preview */}
                {uploadedScreenshots.length > 0 && (
                    <div className="w-full mb-6 flex flex-wrap justify-center gap-2">
                        {uploadedScreenshots.map((shot, idx) => (
                            <div key={idx} className="relative w-16 h-16 rounded-lg border border-[var(--border)] overflow-hidden group/shot">
                                <img src={shot.url} alt="Screenshot" className="w-full h-full object-cover" />
                                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-[8px] text-white text-center py-0.5 truncate px-1">
                                    {PLATFORMS.find(p => p.id === shot.platform)?.label}
                                </div>
                                <button 
                                    onClick={(e) => { e.stopPropagation(); handleRemoveScreenshot(idx); }}
                                    className="absolute top-0 right-0 p-0.5 bg-black/50 hover:bg-red-500 text-white opacity-0 group-hover/shot:opacity-100 transition-opacity"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Fixed Alert Section */}
                <div className="w-full bg-orange-50 border border-orange-100 rounded-xl p-4 flex gap-3 items-start text-left shadow-sm">
                    <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-orange-900 mb-0.5 font-ibm">
                        Minimum 2 Required
                      </p>
                      <p className="text-xs text-orange-800/80 leading-relaxed font-ibm font-medium">
                        Please provide at least one Profile Overview and one Audience Analytics screenshot.
                      </p>
                    </div>
                </div>
                
              </div>
            </div>
          </div>
        </main>

        {/* Footer Action */}
        <footer className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[var(--background)] via-[var(--background)] to-transparent z-50 flex justify-center animate-slide-up delay-400">
          <div className="w-full max-w-2xl flex items-center justify-end">
            <button 
              onClick={handleContinue}
              className="group flex items-center gap-2 bg-[var(--foreground)] hover:bg-[var(--foreground)]/90 text-[var(--background)] px-8 py-3 rounded-xl text-sm font-medium transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5 font-ibm"
            >
              <span>Continue</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </footer>

        {/* Skip Modal */}
        {isSkipping && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-[var(--card)] border border-[var(--border)] rounded-2xl p-6 w-full max-w-sm shadow-2xl animate-slide-up">
                <h3 className="text-lg font-semibold font-cabinet mb-2 text-[var(--foreground)] tracking-tight">
                Skip verification?
                </h3>
                <p className="text-sm text-[var(--muted-foreground)] font-ibm mb-6 leading-relaxed">
                Not providing audience data may limit your score and matching
                eligibility.
                </p>
                <div className="flex gap-3">
                <button 
                    onClick={() => setIsSkipping(false)}
                    className="flex-1 px-4 py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium hover:bg-[var(--muted)] transition-colors text-[var(--foreground)]"
                >
                    Go Back
                </button>
                <button
                    onClick={async () => {
                      // Mark onboarding as complete even if they skip
                      try {
                        await apiClient.updateCurrentUser({ onboarding_completed: true });
                        await checkAuth();
                      } catch (e) {
                        console.error("Failed to update onboarding status", e);
                      }
                      navigate("/dashboard");
                    }}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-[var(--destructive)] text-white text-sm font-medium hover:bg-[var(--destructive)]/90 transition-colors shadow-lg shadow-red-500/20"
                >
                    Skip Anyway
                </button>
                </div>
            </div>
            </div>
        )}
      </div>
    </>
  );
}